import { catchError, forkJoin, map, mergeMap, Observable, of, startWith, Subject, tap } from 'rxjs';
import { Component, inject, OnInit } from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import { CommonModule } from '@angular/common';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ErrorHandlingService } from '@barthauer/abc/core/error-handling';

@Component({
  selector: 'lib-basys-graphics-core-data-access',
  imports: [CommonModule],
  templateUrl: './data-access.component.html',
  styleUrl: './data-access.component.scss',
})
export class DataAccessComponent implements OnInit {
  private pendingDataAccesses = new Map<string, string>();
  private callWebApi$ = new Subject<DataAccessDefinition>();
  readonly accessWebApi$: Observable<boolean>;
  private coreService = inject(AbcCore);
  private resourceService = inject(ResourceManagerService);

  get errorService(): ErrorHandlingService {
    return this.coreService.error;
  }

  constructor() {
    this.accessWebApi$ = this.prepareDataAccess(this.callWebApi$);
  }

  ngOnInit() {}

  async getResourceSet(resourceSet: string): Promise<ResourceSet> {
    return this.resourceService.getResourceSet(resourceSet);
  }

  callWebApi(dataAccess: DataAccessDefinition): void {
    if (dataAccess.begin(this.pendingDataAccesses)) {
      this.callWebApi$.next(dataAccess);
    }
  }

  private prepareDataAccess(accessData$: Subject<DataAccessDefinition>): Observable<boolean> {
    return accessData$.pipe(
      startWith(undefined),
      mergeMap((dataAccess) => {
        if (!dataAccess) {
          return of(true);
        }

        if (dataAccess.wasFirst()) {
          dataAccess.setPending(true);
        }

        if (!dataAccess.isCurrent()) {
          return of(true);
        }

        return forkJoin(dataAccess.dataAccesses.map((dataAccess) => dataAccess())).pipe(
          mergeMap((data) => {
            if (dataAccess.isCurrent()) {
              if (dataAccess.handleResultAsync) {
                return dataAccess.handleResultAsync(data).then(() => true);
              }

              dataAccess.handleResult!(data);
            }

            return of(true);
          }),
          catchError((error) => {
            if (dataAccess.isCurrent() && (!dataAccess.handleError || !dataAccess.handleError(error))) {
              this.coreService.error.publish(error);
            }

            return of(true);
          }),
          tap(() => {
            if (dataAccess.isCurrent()) {
              dataAccess.end();
              dataAccess.setPending(false);
            }
          }),
        );
      }),
    );
  }
}

export class DataAccessDefinition {
  private pendingDataAccesses: Map<string, string>;
  private _wasFirst = false;

  constructor(
    readonly channel: string,
    readonly id: string,
    readonly setPending: (isPending: boolean) => void,
    readonly handleResult: ((data: any[]) => void) | undefined,
    readonly handleResultAsync: ((data: any[]) => Promise<void>) | undefined,
    readonly dataAccesses: (() => Observable<any>)[],
    readonly handleError?: (error: any) => boolean,
  ) {}

  begin(pendingDataAccesses: Map<string, string>): boolean {
    const id = pendingDataAccesses.get(this.channel);
    this._wasFirst = id === undefined;

    if (id !== undefined && id === (this.id ?? '')) {
      return false;
    }

    pendingDataAccesses.set(this.channel, this.id ?? '');
    this.pendingDataAccesses = pendingDataAccesses;

    return true;
  }

  end(): void {
    this.pendingDataAccesses.delete(this.channel);
  }

  isCurrent(): boolean {
    return this.pendingDataAccesses && this.pendingDataAccesses.get(this.channel) === (this.id ?? '');
  }

  wasFirst(): boolean {
    return this._wasFirst;
  }
}
